// ─────────────────────────────────────────────
// Oike · Mi aprendizaje — courses, assignments, submissions and feedback.
//
// Part of Acompañamiento: the coach builds courses (modules → lessons) and
// assigns a published version to a person or to members of a process. Each
// assignment keeps its own copy of that version, and each person keeps their
// own progress, submissions and feedback. The server decides who sees what
// (shared/learning.ts); this file has the keys, labels, limits and the pure
// rules shared by the server, the screens and the tests.
// Values stored in Airtable are the English keys; Spanish labels live here.
// ─────────────────────────────────────────────

export const COURSE_STATUSES = [
  { key: 'draft', label: 'Borrador' },
  { key: 'active', label: 'Publicado' },
  { key: 'archived', label: 'Archivado' },
];

export const LESSON_KINDS = [
  { key: 'video', label: 'Video', hint: 'Un enlace de YouTube, Vimeo o Loom: se reproduce dentro de Oike. Otro proveedor se abre en su sitio.' },
  { key: 'text', label: 'Lectura', hint: 'Texto para leer en Oike.' },
  { key: 'download', label: 'Material descargable', hint: 'Archivos para descargar (PDF, Word, Excel, PowerPoint, imágenes o texto).' },
  { key: 'task', label: 'Trabajo', hint: 'Instrucciones y una entrega escrita, un archivo o ambos. Tú lo revisas.' },
];
export const RESPONSE_TYPES = [
  { key: 'text', label: 'Respuesta escrita' },
  { key: 'file', label: 'Archivo adjunto' },
  { key: 'both', label: 'Escrita y archivo (uno o ambos)' },
];

/** A lesson the person opens and marks as seen. Opening it is "en curso"; only the person marks it "vista". */
export const LESSON_STATES = [
  { key: 'not_started', label: 'Por empezar' },
  { key: 'in_progress', label: 'En curso' },
  { key: 'viewed', label: 'Vista' },
];
/** A task: its latest attempt decides. */
export const SUBMISSION_STATES = [
  { key: 'not_started', label: 'Por empezar' },
  { key: 'draft', label: 'Borrador' },
  { key: 'submitted', label: 'Entregado' },
  { key: 'changes_requested', label: 'Con ajustes solicitados' },
  { key: 'completed', label: 'Completado' },
];
export const ENROLLMENT_STATUSES = [
  { key: 'active', label: 'En curso' },
  { key: 'completed', label: 'Terminado' },
  { key: 'withdrawn', label: 'Retirado' },
];

export const LEARNING_LIMITS = {
  title: 160,
  description: 2000,
  body: 20000,      // a reading
  instructions: 6000,
  answer: 20000,    // a written submission
  feedback: 6000,
  note: 500,        // a version note
  modules: 20,
  lessonsPerModule: 30,
  filesPerLesson: 10,
  filesPerSubmission: 3,
  url: 500,
};

/**
 * Files: stored as Airtable attachments (Coaching Files), uploaded and downloaded
 * only through the server, which checks permissions on every call. Never a
 * direct Airtable link. 4 MB per file: Airtable's upload API takes up to 5 MB
 * and a function request up to 6 MB (base64 adds a third).
 */
export const FILE_MAX_BYTES = 4 * 1024 * 1024;
export const FILE_TYPES = [
  { ext: 'pdf', type: 'application/pdf', label: 'PDF' },
  { ext: 'docx', type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word' },
  { ext: 'xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel' },
  { ext: 'pptx', type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', label: 'PowerPoint' },
  { ext: 'png', type: 'image/png', label: 'PNG' },
  { ext: 'jpg', type: 'image/jpeg', label: 'JPG' },
  { ext: 'jpeg', type: 'image/jpeg', label: 'JPG' },
  { ext: 'txt', type: 'text/plain', label: 'Texto' },
  { ext: 'md', type: 'text/markdown', label: 'Texto' },
];
export const FILE_FORMATS_TEXT = 'PDF, Word, Excel, PowerPoint, PNG, JPG o texto, hasta 4 MB por archivo';
/** The type of a file by its name, or null when it is not accepted. */
export function fileTypeOf(name) {
  const ext = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || '';
  return FILE_TYPES.find(t => t.ext === ext) || null;
}

// ── Videos: embedded only from known providers ──

/** The embeddable player of a video link (YouTube, Vimeo, Loom), or null: then it opens in the provider's site. */
export function videoEmbedOf(url) {
  let u;
  try { u = new URL(String(url || '').trim()); } catch { return null; }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.replace(/^www\.|^m\./, '');
  let m;
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const id = u.pathname === '/watch' ? u.searchParams.get('v') : (u.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]{6,20})/) || [])[1];
    if (id && /^[\w-]{6,20}$/.test(id)) return { provider: 'youtube', label: 'YouTube', src: `https://www.youtube-nocookie.com/embed/${id}?rel=0` };
  }
  if (host === 'youtu.be' && (m = u.pathname.match(/^\/([\w-]{6,20})$/))) return { provider: 'youtube', label: 'YouTube', src: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0` };
  if ((host === 'vimeo.com' || host === 'player.vimeo.com') && (m = u.pathname.match(/(?:^|\/)(\d{6,12})(?:\/([0-9a-f]{6,20}))?\/?$/))) {
    return { provider: 'vimeo', label: 'Vimeo', src: `https://player.vimeo.com/video/${m[1]}${m[2] ? `?h=${m[2]}` : ''}` };
  }
  if (host === 'loom.com' && (m = u.pathname.match(/^\/(?:share|embed)\/([0-9a-f]{16,40})/))) return { provider: 'loom', label: 'Loom', src: `https://www.loom.com/embed/${m[1]}` };
  return null;
}
export const VIDEO_PROVIDERS_TEXT = 'YouTube, Vimeo o Loom';

// ── Progress: from what the person did, never a score ──

/** Every lesson in order, with its module. */
export function lessonsOf(content) {
  return (content?.modules || []).flatMap((mod, mi) => (mod.lessons || []).map((l, li) => ({ ...l, moduleId: mod.id, moduleTitle: mod.title, moduleIndex: mi, index: li })));
}

/** A lesson asks for a submission: a task lesson, or a video, reading or download with a practical task. */
export const lessonHasTask = (l) => l?.kind === 'task' || l?.hasTask === true;

// ── Covers ──
// Since 04-11-2026 (agreed with Ale) a course can have a cover of identity A «Pizarra»: composition A (pizarra oscura,
// la imagen a la derecha) or B (papel, la foto enmarcada a la izquierda), the course name as text and an optional image
// she chooses or uploads (a file of the course, reduced; never generated), with its framing. The earlier drawn covers
// (styles and tones of Oike below) stay for the courses and versions that use them.
export const COVER_LAYOUTS = ['A', 'B'];
export const COVER_LAYOUT_LABELS = { A: 'A · Pizarra', B: 'B · Papel' };
// The earlier drawn covers: with the platform's colors

export const COVER_STYLES = [
  { key: 'waves', label: 'Ondas' },
  { key: 'circles', label: 'Círculos' },
  { key: 'leaves', label: 'Hojas' },
  { key: 'horizon', label: 'Horizonte' },
  { key: 'dots', label: 'Puntos' },
];
/** The platform's area colors (app.css), each with a light and a deep shade. Never the personal brand palette. */
export const COVER_TONES = [
  { key: 'teal', label: 'Verde Oike', light: '#E2F1EE', mid: '#5BBFB5', deep: '#1F7A70' },
  { key: 'sand', label: 'Arena', light: '#FBF0DC', mid: '#E2B66B', deep: '#8A5A12' },
  { key: 'sky', label: 'Cielo', light: '#E5EEF8', mid: '#7FA6D6', deep: '#2D5F99' },
  { key: 'lavender', label: 'Lavanda', light: '#EEEBF8', mid: '#A79BE0', deep: '#5B4E99' },
];
export const DEFAULT_COVER = { style: 'waves', tone: 'teal' };

/** The state of a task from its attempts (latest first decides). */
export function taskStateOf(attempts = []) {
  const last = [...attempts].sort((a, b) => (b.attempt || 0) - (a.attempt || 0))[0];
  return last ? last.status : 'not_started';
}

/**
 * Per lesson state and the next step. A lesson counts as done when the person
 * marked it "vista"; a task, when the coach completed it after review. Playing a
 * video is not learning nor approving: it is never counted.
 */
export function progressOf(content, progress = {}, submissions = []) {
  const lessons = lessonsOf(content);
  const byLesson = new Map();
  for (const s of submissions) { if (!byLesson.has(s.lessonId)) byLesson.set(s.lessonId, []); byLesson.get(s.lessonId).push(s); }
  const states = lessons.map(l => {
    const seen = progress[l.id]?.state === 'viewed' ? 'viewed' : progress[l.id]?.state === 'in_progress' ? 'in_progress' : 'not_started';
    if (lessonHasTask(l)) {
      // A lesson with a task is done when the task is completed after review; opening it is only "en curso"
      const task = taskStateOf(byLesson.get(l.id));
      const state = task === 'not_started' && seen !== 'not_started' ? 'in_progress' : task;
      return { id: l.id, kind: l.kind, hasTask: true, title: l.title, moduleId: l.moduleId, state, done: task === 'completed', waiting: task === 'submitted' };
    }
    return { id: l.id, kind: l.kind, hasTask: false, title: l.title, moduleId: l.moduleId, state: seen, done: seen === 'viewed', waiting: false };
  });
  const done = states.filter(s => s.done).length;
  // Next: the first lesson not done that the person can act on (a task waiting for my review is skipped)
  const next = states.find(s => !s.done && !s.waiting) || null;
  const waiting = states.filter(s => s.waiting).length;
  const changes = states.filter(s => s.state === 'changes_requested').length;
  return { states, total: states.length, done, waiting, changes, next, finished: states.length > 0 && done === states.length };
}

export const learningLabel = (list, key) => list.find(x => x.key === key)?.label || key || '';

/** Ids for modules and lessons: stable across versions, so progress and submissions follow a lesson. */
export const newLearningId = (prefix) => `${prefix}${globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`;
export const LEARNING_ID_RE = /^[ml][a-z0-9]{6,12}$/;
